import {
  Popper,
  Paper,
  Grow,
  ClickAwayListener,
  Box,
  Typography,
  Icon,
  Button,
  useTheme,
} from "@mui/material";
import {Grid} from "@mui/system";
import pxToRem from "hooks/usePxToRem";
import {useEffect, useState} from "react";

export type AlertPopperProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  title: string;
  message: string | React.ReactNode;
  icon?: string;
  color?: "success" | "warning" | "error" | "info" | "primary";
  confirmButtonText?: string;
  onConfirm?: () => void | Promise<void>;
  showCancelButton?: boolean;
  cancelButtonText?: string;
  placement?: "top" | "bottom" | "left" | "right";
  autoHideDuration?: number;
  testId?: string;
  offset?: number[] & {length: 2};
};

const AlertPopper = ({
  open,
  anchorEl,
  onClose,
  title,
  message,
  icon = "info",
  color = "primary",
  confirmButtonText = "OK",
  onConfirm,
  showCancelButton = false,
  cancelButtonText = "Cancelar",
  placement = "bottom",
  autoHideDuration,
  testId,
  offset = [0, 8],
}: AlertPopperProps) => {
  const theme = useTheme();
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (autoHideDuration && open) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [autoHideDuration, open, onClose]);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      sx={{
        zIndex: 1500,
      }}
      modifiers={[
        {
          name: "arrow",
          enabled: true,
          options: {
            element: arrowRef,
          },
        },
        {
          name: "offset",
          options: {
            offset: offset,
          },
        },
      ]}
      data-testid={testId}
    >
      {({TransitionProps}) => (
        <ClickAwayListener onClickAway={onClose}>
          <Grow {...TransitionProps}>
            <Paper
              elevation={4}
              sx={{
                maxWidth: pxToRem(400),
                borderRadius: pxToRem(8),
                overflow: "hidden",
                // transform: `translate(-20px,0)`,
              }}
            >
              <Box
                ref={setArrowRef}
                className="MuiPopper-arrow"
                sx={{position: "absolute"}}
              />

              <Grid container p={pxToRem(16)} spacing={2}>
                <Grid
                  size={{xs: 12}}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box
                    sx={{
                      minHeight: pxToRem(40),
                      minWidth: pxToRem(40),
                      borderRadius: pxToRem(5),
                      backgroundColor: theme.palette[color]?.["50"],
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Icon
                      sx={{
                        color: theme.palette[color]?.["700"],
                        fontSize: pxToRem(24),
                      }}
                    >
                      {icon}
                    </Icon>
                  </Box>
                  <Typography variant="h6">{title}</Typography>
                </Grid>

                <Grid size={{xs: 12}}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{whiteSpace: "pre-line"}}
                  >
                    {message}
                  </Typography>
                </Grid>

                <Grid
                  size={{xs: 12}}
                  display="flex"
                  justifyContent="flex-end"
                  gap={1}
                >
                  {showCancelButton && (
                    <Button
                      variant="outlined"
                      onClick={onClose}
                      size="small"
                      color={color}
                    >
                      {cancelButtonText}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleConfirm}
                    color={color}
                    size="small"
                  >
                    {confirmButtonText}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grow>
        </ClickAwayListener>
      )}
    </Popper>
  );
};

export default AlertPopper;
